import React, { useState, useRef, useEffect } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useTranslation } from "react-i18next";
import UtilMethods from "../../util/UtilMethods";
import { savePrayerRequest } from "../../services/PrayerServices";
import { franc } from "franc-min";
import iso6391 from "iso-639-1";
import AlertModal from "../common/AlertModal"; // Ensure this import is correct
import { COUNTRY_BY_LANGUAGE } from "../common/Constants";
import i18n from "i18next";

const PrayerRequestForm = ({ onSuccess, language }) => {
  const { t } = useTranslation("prayerRequest");
  const alertTimeoutRef = useRef(null);
  const browserCountry = navigator.language.split("-")[1]; // "US", "MX", "FR"
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prayerRequest, setPrayerRequest] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    language: "",
    messageInEnglish: "",
    errors: {},
    emailSent: false,
    alertMessage: "",
    alertType: "",
  });
  const [showModal, setShowModal] = useState(false); // Track modal visibility
  const [modalType, setModalType] = useState("");

  const [phoneCountry, setPhoneCountry] = useState(COUNTRY_BY_LANGUAGE[language || i18n.language.slice(0, 2)] || browserCountry || "US");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPrayerRequest((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  useEffect(() => {
    return () => {
     setPhoneCountry(COUNTRY_BY_LANGUAGE[language || i18n.language.slice(0, 2)] || browserCountry || "US");
     console.log("PrayerRequestForm useEffect phoneCountry:", phoneCountry);
    };
  }, [language]);

  const detectLanguage = async (text) => {
    const isoLangCode = iso6391.getCode(franc(text, { minLength: 3 })) || "und";
    if (isoLangCode === "und") {
      setPrayerRequest((prevState) => ({
        ...prevState,
        language: null,
        messageInEnglish: text,
      }));
    } else {
      setPrayerRequest((prevState) => ({
        ...prevState,
        language: isoLangCode,
      }));
    }
  };

  const validateForm = () => {
    const { firstName, lastName, email, message, phone } = prayerRequest;
    const errors = {};

    if (!firstName) errors.firstName = "First Name is required";
    if (!lastName) errors.lastName = "Last Name is required";
    if (!email || !UtilMethods.validateEmail(email))
      errors.email = "Please enter a valid Email";
    if (!message) errors.message = "Prayer Request is required";
    if (!phone || !UtilMethods.validatePhone(phone))
      errors.phone = "Please enter a valid Phone Number";

    setPrayerRequest((prevState) => ({
      ...prevState,
      errors,
    }));

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    setIsSubmitting(true);
    e.preventDefault();

    if (validateForm()) {
      try {
        const { message } = prayerRequest;
        await detectLanguage(message);
        // Show confirmation modal before submitting
        setPrayerRequest((prevState) => ({
          ...prevState,
          messageInEnglish: message,
        }));
        setShowModal(true);
        setModalType("confirmation");
      } catch (error) {
        console.error("API error:", error);
        showAlert(t("alert.failure"), "danger");
        setIsSubmitting(false);
      }
    } else {
      showAlert(t("alert.validation"), "danger");
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    console.log("handleCancel called");
    resetForm();
    setShowModal(false); // Close the modal if cancel is clicked
    setModalType(""); // Reset the modal type
    if (onSuccess) onSuccess();
    //window.location.href = "/";
  };

  const handleCancel = () => {
    console.log("handleCancel called");
    resetForm();

    //window.location.href = "/";
  };

  const cancelSubmit = () => {
    setIsSubmitting(false);
    setShowModal(false); // Close the modal if cancel is clicked
    setModalType(""); // Reset the modal type
    setPrayerRequest((prevState) => ({
      ...prevState,
      alertType: "",
    }));

    // Reset the alert message if cancel is clicked
  };

  const showAlert = (message, type) => {
    setPrayerRequest((prevState) => ({
      ...prevState,
      alertMessage: message,
      alertType: type,
    }));
    console.log("showAlertAlert message:", message);
    console.log("showAlert Alert type:", type);
    if (type !== "danger") {
      console.log("showAlert Alert type iff:", type);
      setShowModal(true);
      setModalType(type);
    }
    if (alertTimeoutRef.current) clearTimeout(alertTimeoutRef.current);
    alertTimeoutRef.current = setTimeout(() => {
      setPrayerRequest((prevState) => ({
        ...prevState,
        alertMessage: "",
        alertType: "",
      }));
      // handleClose();
    }, 5000);
  };

  const confirmSubmit = async () => {
    try {
      setShowModal(false); // Close the confirmation modal before submission
      const response = await savePrayerRequest(prayerRequest);
      setModalType("success");
      setShowModal(true); // Show the success modal
      console.log("Form submitted successfully:", response);
    } catch (error) {
      console.error("API error:", error);
      setModalType("");
      setShowModal(false); // Close the modal if there's an error
      showAlert(t("alert.failure"), "danger");
    }
    setIsSubmitting(false);
  };

  const resetForm = () => {
    setPrayerRequest({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      message: "",
      language: "",
      messageInEnglish: "",
      errors: {},
      emailSent: false,
      alertMessage: "",
      alertType: "",
    });
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="contact-form mt-3">
      {prayerRequest.alertMessage && (
        <div className={`alert alert-${prayerRequest.alertType}`} role="alert">
          {prayerRequest.alertMessage}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() =>
              setPrayerRequest({
                ...prayerRequest,
                alertMessage: "",
                alertType: "",
              })
            }
          ></button>
        </div>
      )}
      {/* First Name Input */}
      <div className="form-group mb-2">
        <div className="row">
          <div className="col-12 col-md-6 mb-2 mb-md-0">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-user"></i>
              </span>
              <input
                type="text"
                className={`form-control ${prayerRequest.errors.firstName ? "is-invalid" : ""
                  }`}
                placeholder={t("placeholders.firstName")}
                name="firstName"
                value={prayerRequest.firstName}
                onChange={handleChange}
                required
              />
            </div>
            {prayerRequest.errors.firstName && (
              <span className="text-danger">
                {prayerRequest.errors.firstName}
              </span>
            )}
          </div>

          {/* Last Name Input */}
          <div className="col-12 col-md-6">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-user"></i>
              </span>
              <input
                type="text"
                className={`form-control ${prayerRequest.errors.lastName ? "is-invalid" : ""
                  }`}
                placeholder={t("placeholders.lastName")}
                name="lastName"
                value={prayerRequest.lastName}
                onChange={handleChange}
                required
              />
            </div>
            {prayerRequest.errors.lastName && (
              <span className="text-danger">
                {prayerRequest.errors.lastName}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Email and Phone Inputs */}
      <div className="form-group mb-2">
        <div className="row">
          <div className="col-12 col-md-6 mb-2 mb-md-0">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-envelope"></i>
              </span>
              <input
                type="email"
                className={`form-control ${prayerRequest.errors.email ? "is-invalid" : ""
                  }`}
                placeholder={t("placeholders.email")}
                name="email"
                value={prayerRequest.email}
                onChange={handleChange}
                required
              />
            </div>
            {prayerRequest.errors.email && (
              <span className="text-danger">{prayerRequest.errors.email}</span>
            )}
          </div>

          {/* Phone Input */}
          <div className="col-12 col-md-6">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-phone"></i>
              </span>
              <PhoneInput
                defaultCountry={phoneCountry}
                international
                flagUrl="https://flagcdn.com/w320/{xx}.png"
                placeholder={t("placeholders.phone")}
                value={prayerRequest.phone}
                onChange={(value) =>
                  setPrayerRequest((prevState) => ({
                    ...prevState,
                    phone: value,
                  }))
                }
                className={`form-select ${prayerRequest.errors.phone ? "is-invalid" : ""
                  }`}
                required
              />
            </div>
            {prayerRequest.errors.phone && (
              <span className="text-danger">{prayerRequest.errors.phone}</span>
            )}
          </div>
        </div>
      </div>

      {/* Message Input */}
      <div className="form-group mb-2">
        <div className="row">
          <div className="col-12">
            <div className="input-group">
              <span className="input-group-text" style={{ minHeight: "110px" }}>
                <i className="fas fa-comments"></i>
              </span>
              <textarea
                className={`form-control ${prayerRequest.errors.message ? "is-invalid" : ""
                  }`}
                placeholder={t("placeholders.prayerRequest")}
                name="message"
                value={prayerRequest.message}
                onChange={handleChange}
                required
                style={{ minHeight: "110px" }}
              />
            </div>
            {prayerRequest.errors.message && (
              <span className="text-danger">
                {prayerRequest.errors.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mt-3 mb-2">
        <button
          type="submit"
          className="btn btn-primary flex-grow-1 order-1 order-md-2"
          disabled={isSubmitting}
        >
          {t("buttons.submit")}
        </button>
        <button
          type="button"
          className="btn btn-light flex-grow-1 order-2 order-md-1"
          onClick={handleCancel}
        >
          {t("buttons.cancel")}
        </button>
      </div>

      {/* Alert Modal */}
      <AlertModal
        show={showModal}
        type={modalType}
        title={
          modalType === "confirmation"
            ? t("modal.title")
            : t("modal.titleSuccess")
        }
        message={
          modalType === "confirmation"
            ? t("modal.message")
            : t("alert.success")
        }
        details={
          modalType === "confirmation"
            ? {
              "First Name": prayerRequest.firstName,
              "Last Name": prayerRequest.lastName,
              Email: prayerRequest.email,
              Phone: prayerRequest.phone,
              Message: prayerRequest.message,
            }
            : {}
        }
        onCancel={modalType === "confirmation" ? cancelSubmit : handleClose}
        onConfirm={confirmSubmit}
      />
    </form>
  );
};

export default PrayerRequestForm;
